import { getToken } from '../authentication/authProvider';

/** A booked Canada Post pickup, as the NestJS API returns it. */
export interface ScheduledPickup {
  id: string;
  requestId: string;
  status: 'scheduled' | 'cancelled' | 'completed';
  partnerId: string | null;
  locationName: string;
  locationAddress: string;
  locationCity: string;
  locationProvince: string;
  locationPostalCode: string;
  /** YYYY-MM-DD. */
  pickupDate: string;
  /** HH:MM. */
  preferredTime: string;
  /** HH:MM. */
  closingTime: string;
  pickupVolume: string | null;
  pickupInstructions: string | null;
  cpStatus: string | null;
  createdBy: string | null;
  createdAt: string;
  cancelledAt: string | null;
}

export interface PickupAvailability {
  postalCode: string;
  onDemandTour: boolean;
  onDemandCutoff: string | null;
  scheduledPickupsAvailable: boolean;
}

export interface PickupRequestPayload {
  partnerId?: string;
  pickupDate: string;
  preferredTime: string;
  closingTime: string;
  pickupVolume?: string;
  pickupInstructions?: string;
}

const apiBase = () => {
  const apiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
  return apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
};

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = await getToken();
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({} as { message?: string | string[] }));
    // Nest sends validation failures as an array of messages.
    const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    throw new Error(message || `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
};

export const fetchUpcomingPickups = () => request<ScheduledPickup[]>('/canada-post/pickups');

export const fetchPickupAvailability = (postalCode: string) =>
  request<PickupAvailability>(
    `/canada-post/pickup/availability?postalCode=${encodeURIComponent(postalCode)}`,
  );

export const schedulePickup = (payload: PickupRequestPayload) =>
  request<ScheduledPickup>('/canada-post/pickup', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const modifyPickup = (id: string, payload: PickupRequestPayload) =>
  request<ScheduledPickup>(`/canada-post/pickup/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const cancelPickup = (id: string) =>
  request<ScheduledPickup>(`/canada-post/pickup/${id}`, { method: 'DELETE' });
